import {
  Typography,
  Box,
  Container,
  Grid,
  Card,
  Divider,
  Theme,
} from "@mui/material";
import { motion } from "framer-motion";
import React from "react";
import ContentSection from "../ContentSection";
import { useTranslation } from "react-i18next";

interface AdminContentProps {
  theme: Theme;
  getBannerImage: () => string;
}

const AdminContent: React.FC<AdminContentProps> = ({
  theme,
  getBannerImage,
}) => {
  const { t } = useTranslation();

  return (
    <Container
      maxWidth={false}
      sx={{
        py: 4,
        px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 },
        maxWidth: "100%",
        mx: "auto",
      }}
    >
      {/* Main card */}
      <Card
        elevation={2}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          bgcolor: "white",
          maxWidth: "100%",
          mx: "auto",
        }}
      >
        {/* Banner image */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: { xs: 250, sm: 350, md: 400, lg: 450, xl: 500 },
          }}
        >
          <img
            src={getBannerImage()}
            alt="Admin Portal"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              background:
                "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)",
              p: { xs: 3, sm: 4, md: 5 },
            }}
          >
            <Typography
              variant="h3"
              fontWeight={700}
              color="white"
              gutterBottom
              sx={{ fontSize: { xs: "1.75rem", sm: "2.25rem", md: "2.75rem" } }}
            >
              Welcome to the CYPMS Admin Portal
            </Typography>
            <Typography
              variant="h5"
              fontWeight={500}
              color="white"
              sx={{ fontSize: { xs: "1rem", sm: "1.25rem", md: "1.5rem" } }}
            >
              Powering Program Management
            </Typography>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
          <AdminIntroSection />
          <Divider sx={{ my: 4 }} />
          <AdminFeaturesSection theme={theme} />
          <Divider sx={{ my: 4 }} />
          <AdminToolsSection theme={theme} />
        </Box>
      </Card>
    </Container>
  );
};

// Admin intro section
const AdminIntroSection: React.FC = () => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.7 }}
    >
      <Typography
        variant="h4"
        fontWeight={700}
        gutterBottom
        color="primary"
        sx={{ fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" } }}
      >
        What Is CYPMS?
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }} color="text.secondary">
        The CODICE Youth Program Management System (CYPMS) is a modern, secure
        platform that equips DC government staff with the tools to manage,
        oversee, and optimize youth employment programs like MBSYEP and beyond.
      </Typography>
      <Typography variant="body1" sx={{ mb: 4 }} color="text.secondary">
        Whether you're tracking registrations, approving documents, managing
        payroll files, or extracting reports — the Admin Portal provides
        everything in one integrated environment.
      </Typography>
    </motion.div>
  );
};

interface SectionWithThemeProps {
  theme: Theme;
}

// Admin features section
const AdminFeaturesSection: React.FC<SectionWithThemeProps> = ({ theme }) => {
  return (
    <>
      <Typography
        variant="h4"
        fontWeight={700}
        gutterBottom
        color="primary"
        sx={{ fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" } }}
      >
        Things You Should Know About CYPMS
      </Typography>
      <Typography variant="body1" sx={{ mb: 3 }} color="text.secondary">
        Here's what you can do once logged into the Admin Portal:
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Monitor Program Activity via Dashboard"
            content="Access real-time data on youth registrations, host enrollments, pending approvals, timesheet statuses, and more—all in one snapshot."
            delay={0.1}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Review and Approve Documentation"
            content="Approve or reject documents submitted by youth and hosts including consent forms, proof of eligibility, W-4/D-4 forms, transfer and termination requests."
            delay={0.2}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Manage Payroll Submission"
            content="Compile and submit approved timesheet data to payroll processors like Paycor/ADP and prepare compliant files for submission to the District's financial system (DIFS)."
            delay={0.3}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Access and Export Reports"
            content="Generate detailed reports on participation, jobs, payroll, and compliance. Use filters to drill down into trends and export data for analysis or auditing."
            delay={0.4}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="View Youth and Host Profiles"
            content="Search and view comprehensive records of both youth participants and host organizations, including submitted forms, activity history, and approval status."
            delay={0.5}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Create and Manage Onboarding Tools"
            content="Build Orientation, Banking, and Certification quizzes that youth complete during registration and onboarding."
            delay={0.6}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Create and Manage Admin Users"
            content="Add new admin users, assign roles and permissions, and control access to specific system features."
            delay={0.7}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Send Communications"
            content="Broadcast announcements, alerts, and instructions to youth, hosts, or other admins using the integrated messaging system."
            delay={0.8}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <ContentSection
            theme={theme}
            title="Login via Active Directory / OKTA"
            content="DOES Admins can log in using secure Single Sign-On via Active Directory or OKTA, ensuring centralized and secure authentication."
            delay={0.9}
          />
        </Grid>
      </Grid>
    </>
  );
};

// Admin tools section
const AdminToolsSection: React.FC<SectionWithThemeProps> = ({ theme }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.9 }}
    >
      <Card
        elevation={1}
        sx={{
          p: 4,
          borderRadius: 2,
          bgcolor: theme.palette.grey[50],
          border: `1px solid ${theme.palette.grey[200]}`,
        }}
      >
        <Typography variant="h5" fontWeight={700} gutterBottom color="primary">
          What Happens After Login?
        </Typography>
        <Box component="ul" sx={{ pl: 4, mb: 0 }}>
          <Box component="li" sx={{ mb: 1, color: "text.secondary" }}>
            Monitor Key Activity – Use the dashboard to track system-wide
            progress
          </Box>
          <Box component="li" sx={{ mb: 1, color: "text.secondary" }}>
            Review Approvals – Process forms, verify documents, and manage
            exceptions
          </Box>
          <Box component="li" sx={{ mb: 1, color: "text.secondary" }}>
            Manage Payroll and Reporting – Submit, track, and verify payroll
            data
          </Box>
          <Box component="li" sx={{ mb: 1, color: "text.secondary" }}>
            Support System Operations – Create users, programs, quizzes, and
            more
          </Box>
          <Box component="li" sx={{ mb: 0, color: "text.secondary" }}>
            Communicate Efficiently – Keep stakeholders informed with timely
            messages
          </Box>
        </Box>
      </Card>
    </motion.div>
  );
};

export default AdminContent;
